import * as React from "react";
import { useCallback, useContext, useMemo, useState, useSyncExternalStore } from "react";
import { FileWarning, Loader2, Paperclip } from "lucide-react";
import { AttachmentChip } from "./AttachmentChip";

import type { UploadTransport } from "../uploads/uploadTransport";
import { useComposerUploads } from "../uploads/useComposerUploads";
import { getUserFilesScopeGeneration, subscribeToUserFiles } from "../api/finster/userFilesStore";

const UPLOAD_FILE_EXTENSIONS = new Set([
  "pdf",
  "xlsx",
  "csv",
  "docx",
  "pptx",
  "eml",
  "msg",
  "jpg",
  "jpeg",
  "png",
]);

function isSupportedUploadFile(file: File): boolean {
  const name = file.name.trim().toLowerCase();
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return false;
  return UPLOAD_FILE_EXTENSIONS.has(name.slice(dot + 1));
}

export function unsupportedChosenFiles(files: readonly File[]): File[] {
  return files.filter((file) => !isSupportedUploadFile(file));
}

function rejectedFileId(name: string): string {
  const cryptoLike = globalThis.crypto as { randomUUID?: () => string } | undefined;
  const unique =
    typeof cryptoLike?.randomUUID === "function"
      ? cryptoLike.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${name}-${unique}`;
}

export interface RejectedComposerFile {
  id: string;
  name: string;
}

interface ComposerUploads {
  rejectedFiles: readonly RejectedComposerFile[];
  progress: ReturnType<typeof useComposerUploads>;
  addRejectedFiles: (files: readonly File[]) => void;
  dismissRejectedFile: (id: string) => void;
}

const ComposerUploadsContext = React.createContext<ComposerUploads | undefined>(undefined);

export function useComposerUploadProgress() {
  return useContext(ComposerUploadsContext)?.progress;
}

export function useComposerUploadContext(): ComposerUploads {
  const value = useContext(ComposerUploadsContext);
  if (!value) {
    throw new Error("useComposerUploadContext must be used within ComposerUploadBoundary");
  }
  return value;
}

/**
 * Owns local upload progress and unsupported-file chips for both composers.
 * A transport must be supplied before supported uploads can start.
 */
export function ComposerUploadBoundary(props: {
  children: React.ReactNode;
  transport?: UploadTransport;
}) {
  const generation = useSyncExternalStore(
    subscribeToUserFiles,
    getUserFilesScopeGeneration,
    getUserFilesScopeGeneration
  );
  return <ScopedUploadBoundary key={generation} {...props} />;
}

function ScopedUploadBoundary({
  children,
  transport,
}: {
  children: React.ReactNode;
  transport?: UploadTransport;
}) {
  const progress = useComposerUploads(transport);
  const [rejectedFiles, setRejectedFiles] = useState<RejectedComposerFile[]>([]);

  const addRejectedFiles = useCallback((files: readonly File[]) => {
    if (files.length === 0) return;
    setRejectedFiles((current) => [
      ...current,
      ...files.map((file) => ({
        id: rejectedFileId(file.name),
        name: file.name,
      })),
    ]);
  }, []);

  const dismissRejectedFile = useCallback((id: string) => {
    setRejectedFiles((current) => current.filter((file) => file.id !== id));
  }, []);

  const value = useMemo(
    () => ({ rejectedFiles, addRejectedFiles, dismissRejectedFile, progress }),
    [addRejectedFiles, dismissRejectedFile, rejectedFiles, progress]
  );

  return (
    <ComposerUploadsContext.Provider value={value}>{children}</ComposerUploadsContext.Provider>
  );
}

export function ComposerUploadChips({ disabled = false }: { disabled?: boolean }) {
  const { rejectedFiles, dismissRejectedFile, progress } = useComposerUploadContext();

  return (
    <>
      {progress.uploads.map((file) => (
        <AttachmentChip
          key={file.localId}
          icon={
            file.ready ? (
              <Paperclip />
            ) : (
              <Loader2 className="animate-spin motion-reduce:animate-none" />
            )
          }
          label={file.name}
          ariaLabel={file.ready ? file.name : `Uploading ${file.name}`}
          onRemove={file.ready ? () => progress.removeUpload(file.localId) : undefined}
          disabled={disabled}
        />
      ))}
      {rejectedFiles.map((file) => (
        <AttachmentChip
          key={file.id}
          icon={<FileWarning />}
          label={file.name}
          ariaLabel="Unsupported file"
          onRemove={() => dismissRejectedFile(file.id)}
          disabled={disabled}
        />
      ))}
    </>
  );
}
